import { Alert, Box, Stack } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";

import API from "../../api";
import TableHosts from "./components/tableHosts";
import IntelligenceTimeFilter from "../../components/IntelligenceTimeFilter";
import Header from "../../components/Header";

/*
 * Restituisce il filtro temporale iniziale.
 *
 * Se nella query string sono presenti dateFrom e dateTo
 * ed entrambi sono validi, vengono utilizzati.
 *
 * Altrimenti vengono utilizzati gli ultimi 3 giorni.
 */
const getInitialTimeFilter = () => {
  const defaultDateFrom = dayjs().subtract(3, "day");
  const defaultDateTo = dayjs();

  const searchParams = new URLSearchParams(window.location.search);

  const dateFromParam = searchParams.get("dateFrom");
  const dateToParam = searchParams.get("dateTo");

  const parsedDateFrom = dateFromParam ? dayjs(dateFromParam) : null;
  const parsedDateTo = dateToParam ? dayjs(dateToParam) : null;

  const hasValidDates =
    parsedDateFrom?.isValid() &&
    parsedDateTo?.isValid();

  return {
    dateFrom: hasValidDates
      ? parsedDateFrom
      : defaultDateFrom,

    dateTo: hasValidDates
      ? parsedDateTo
      : defaultDateTo,
  };
};

/*
 * Restituisce il filtro Severity iniziale leggendo
 * il parametro "severity" dalla query string.
 *
 * Mapping severity:
 *
 * 0 = High
 * 1 = Critical
 * 2 = Alert
 * 3 = Movement
 *
 * Parametri URL supportati:
 *
 * ?severity=high
 * ?severity=alert
 *
 * Se il parametro non è presente oppure contiene
 * un valore non supportato, non viene applicato
 * alcun filtro severity.
 */
const getInitialSeverityFilter = () => {
  const searchParams = new URLSearchParams(window.location.search);

  const severityParam = searchParams.get("severity");

  if (!severityParam) {
    return [];
  }

  const normalizedSeverity = severityParam
    .trim()
    .toLowerCase();

  const severityMap = {
    high: 0,
    alert: 2,
  };

  const severityValue =
    severityMap[normalizedSeverity];

  if (severityValue === undefined) {
    return [];
  }

  return [severityValue];
};

const Aggregations = () => {
  const [aggregationHosts, setAggregationHosts] = useState([]);

  const [isDetailsOpen, setIsDetailsOpen] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  /*
   * Filtro temporale.
   *
   * Di default:
   * ultimi 3 giorni.
   *
   * Se dateFrom/dateTo sono presenti nella URL:
   * utilizza quelli.
   */
  const [timeFilter, setTimeFilter] =
    useState(getInitialTimeFilter);

  /*
   * Severity iniziale derivata dalla query string.
   *
   * Viene letta solamente durante l'inizializzazione
   * della pagina.
   */
  const [initialSeverity] =
    useState(getInitialSeverityFilter);

  /*
   * Payload per il recupero delle Aggregations.
   */
  const aggregationPayload = useMemo(
    () => ({
      aggregation_dimension: "host",

      timestamp: {
        dateFrom: timeFilter.dateFrom.toISOString(),
        dateTo: timeFilter.dateTo.toISOString(),
      },
    }),
    [timeFilter]
  );

  /*
   * Recupero Aggregations.
   */
  useEffect(() => {
    setLoading(true);
    setLoadError(false);

    API.getAggregations(aggregationPayload)
      .then((res) => {
        setAggregationHosts(
          Array.isArray(res?.data)
            ? res.data
            : []
        );
      })
      .catch((error) => {
        setLoadError(true);
        console.error(
          "[Aggregations] getAggregations error:",
          error
        );

        setAggregationHosts([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [aggregationPayload]);

  /*
   * NOTE:
   * "Access Failed" non serve più.
   *
   * La query rimane commentata per un'eventuale
   * riattivazione futura.
   */

  // const payloadAccessFailed = useMemo(
  //   () => ({
  //     timestamp: {
  //       dateFrom:
  //         timeFilter.dateFrom.toISOString(),
  //
  //       dateTo:
  //         timeFilter.dateTo.toISOString(),
  //     },
  //   }),
  //   [timeFilter]
  // );

  // const [
  //   aggregationAccessFailed,
  //   setAggregationAccessFailed,
  // ] = useState([]);

  // useEffect(() => {
  //   API.getAggregationsAccessFailed(
  //     payloadAccessFailed
  //   )
  //     .then((res) => {
  //       setAggregationAccessFailed(
  //         Array.isArray(res?.data)
  //           ? res.data
  //           : []
  //       );
  //     })
  //     .catch(() => {
  //       setAggregationAccessFailed([]);
  //     });
  // }, [payloadAccessFailed]);

  return (
    <Box
      sx={{
        px: {
          xs: 2,
          md: 3,
        },
        py: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Stack spacing={2.5}>
        <Header
          title="Aggregations"
          subtitle=""
          desc="Host groupings with related timeline events. Filter by time range to explore the detected activity and the timeline (Crono) per host."
          sx={{
            width: {
              xs: "100%",
              md: "auto",
            },
          }}
        />

        {!isDetailsOpen && (
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            justifyContent="flex-start"
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
            spacing={1.5}
          >
            <IntelligenceTimeFilter
              initialDateFrom={
                timeFilter.dateFrom
              }
              initialDateTo={
                timeFilter.dateTo
              }
              onDateChange={
                setTimeFilter
              }
              maxTimeRange={{
                value: 7,
                type: "day",
              }}
            />
          </Stack>
        )}

        {loadError && (
          <Alert severity="error">
            Unable to load aggregations. Check the connection and try again by changing the time range.
          </Alert>
        )}

        {/* sempre montata: durante il caricamento la griglia mostra lo skeleton */}
        <TableHosts
            loading={loading}
            data={aggregationHosts}
            title="Hosts"
            timeFilter={timeFilter}
            initialSeverity={
              initialSeverity
            }
            onDetailsOpenChange={
              setIsDetailsOpen
            }
          />
      </Stack>
    </Box>
  );
};

export default Aggregations;